import React, { useEffect, useRef, useState } from "react";
import { Button, DatePicker, Empty, Input, Select, Spin, Table, Typography, message } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getAirportDepartureBoard } from "../../api/v3/airportDepartureApi";
import "./airport-departure-board.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Title } = Typography;

const STATUS_OPTIONS = [
    { value: "all", label: "全部" },
    { value: "scheduled", label: "计划" },
    { value: "estimated", label: "预计变更" },
    { value: "delayed", label: "延误" },
    { value: "departed", label: "已起飞" },
    { value: "cancelled", label: "取消" },
    { value: "diverted", label: "备降" },
    { value: "unknown", label: "未知" },
];

const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((item) => [item.value, item.label]));
const SOURCE_LABEL = {
    flightradar24: "FR24 观察快照",
};

const EMPTY_SUMMARY = {
    total: 0,
    departed: 0,
    with_registration: 0,
    top_destinations: [],
};

function todayLocalDate() {
    return dayjs().format("YYYY-MM-DD");
}

function formatAirportLabel(airport) {
    if (!airport) return "—";
    const code = airport.airport_iata_code || "";
    const name = (airport.airport_name || "").trim();
    return name ? `${code} · ${name}` : code;
}

function formatClock(value, timeZone) {
    if (!value) return "—";
    const parsed = dayjs.utc(value);
    if (!parsed.isValid()) return "—";
    if (timeZone) {
        try {
            return parsed.tz(timeZone).format("HH:mm");
        } catch {
            return parsed.format("HH:mm");
        }
    }
    return parsed.format("HH:mm");
}

function formatDateTime(value) {
    if (!value) return "暂无更新时间";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : "暂无更新时间";
}

function displayText(value, emptyLabel = "—") {
    const text = value == null ? "" : String(value).trim();
    return text || emptyLabel;
}

function formatDestination(row) {
    const code = (row.destination_iata || "").trim();
    const city = (row.destination_city || "").trim();
    if (code && city) return `${code}  ${city}`;
    if (code) return code;
    if (city) return city;
    return "—";
}

function statusLabel(status) {
    if (!status || !STATUS_LABEL[status]) return "未知";
    return STATUS_LABEL[status];
}

function sourceLabel(source) {
    if (!source) return "观察快照";
    return SOURCE_LABEL[source] || `${source} 观察快照`;
}

function errorMessage(cause, fallback) {
    return cause?.response?.data?.detail || cause?.response?.data?.message || cause?.message || fallback;
}

export default function AirportDepartureBoardPage() {
    const [businessDate, setBusinessDate] = useState(todayLocalDate);
    const [selectedAirport, setSelectedAirport] = useState(null);
    const [status, setStatus] = useState("all");
    const [searchInput, setSearchInput] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [board, setBoard] = useState(null);
    const [expandedFlightId, setExpandedFlightId] = useState(null);
    const requestSeqRef = useRef(0);

    useEffect(() => {
        const seq = ++requestSeqRef.current;
        let cancelled = false;

        async function loadBoard() {
            setLoading(true);
            try {
                const params = {
                    business_date: businessDate,
                    airport_iata_code: selectedAirport || undefined,
                    status: status === "all" ? undefined : status,
                    q: appliedSearch || undefined,
                };
                const payload = await getAirportDepartureBoard(params);
                if (cancelled || seq !== requestSeqRef.current) return;
                setBoard(payload);
                setExpandedFlightId(null);
                if (selectedAirport && !payload.selected?.airport_iata_code) {
                    setSelectedAirport(null);
                }
            } catch (error) {
                if (cancelled || seq !== requestSeqRef.current) return;
                message.error(errorMessage(error, "加载机场离港观察失败"));
            } finally {
                if (!cancelled && seq === requestSeqRef.current) {
                    setLoading(false);
                }
            }
        }

        loadBoard();
        return () => {
            cancelled = true;
        };
    }, [businessDate, selectedAirport, status, appliedSearch]);

    const airports = board?.airports || [];
    const summary = board?.summary || EMPTY_SUMMARY;
    const flights = board?.flights || [];
    const meta = board?.meta;
    const currentCode = selectedAirport || board?.selected?.airport_iata_code;
    const currentAirport = airports.find((item) => item.airport_iata_code === currentCode) || null;
    const airportTimeZone = currentAirport?.schedule_timezone || null;
    const expandedFlight = flights.find((item) => item.id === expandedFlightId) || null;

    const handleSelectAirport = (code) => {
        if (code === selectedAirport) return;
        setSelectedAirport(code);
    };

    const handleQuery = () => {
        setAppliedSearch(searchInput.trim());
    };

    const handleReset = () => {
        setStatus("all");
        setSearchInput("");
        setAppliedSearch("");
    };

    const handleRowClick = (record) => {
        setExpandedFlightId((current) => (current === record.id ? null : record.id));
    };

    const columns = [
        {
            title: "计划时间",
            dataIndex: "scheduled_departure_at",
            key: "scheduled_departure_at",
            width: 100,
            render: (value) => formatClock(value, airportTimeZone),
        },
        {
            title: "航班号",
            dataIndex: "flight_number",
            key: "flight_number",
            width: 110,
            render: (value) => displayText(value),
        },
        {
            title: "目的地",
            key: "destination",
            render: (_, row) => formatDestination(row),
        },
        {
            title: "状态",
            dataIndex: "status_normalized",
            key: "status_normalized",
            width: 100,
            render: (value) => statusLabel(value),
        },
        {
            title: "机型",
            dataIndex: "aircraft_type_code",
            key: "aircraft_type_code",
            width: 90,
            render: (value) => displayText(value),
        },
        {
            title: "注册号",
            dataIndex: "aircraft_registration",
            key: "aircraft_registration",
            width: 110,
            render: (value) => displayText(value, "—"),
        },
    ];

    return (
        <div className="adb-page">
            <div className="adb-header">
                <Title level={3} className="adb-title">
                    机场离港观察
                </Title>
                <Text type="secondary">只读查看已入库的第三方离港观察，不等于公司正式航班执行记录</Text>
            </div>

            <div className="adb-layout">
                <aside className="adb-side">
                    <section className="adb-block">
                        <h3>机场切换</h3>
                        {airports.length === 0 && !loading ? (
                            <Empty description="暂无可用机场配置" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <div className="adb-airport-list">
                                {airports.map((airport) => {
                                    const active = airport.airport_iata_code === currentCode;
                                    return (
                                        <button
                                            key={airport.config_id}
                                            type="button"
                                            className={`adb-airport${active ? " is-active" : ""}`}
                                            aria-current={active ? "true" : undefined}
                                            onClick={() => handleSelectAirport(airport.airport_iata_code)}
                                        >
                                            <strong>{formatAirportLabel(airport)}</strong>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    <section className="adb-block">
                        <h3>业务日期</h3>
                        <DatePicker
                            allowClear={false}
                            value={dayjs(businessDate)}
                            onChange={(value) => {
                                if (!value) return;
                                setBusinessDate(value.format("YYYY-MM-DD"));
                            }}
                            style={{ width: "100%" }}
                        />
                    </section>

                    <section className="adb-block">
                        <h3>今日态势</h3>
                        <div className="adb-kpi">
                            <div className="adb-kpi-item">
                                <span>班次</span>
                                <strong>{summary.total}</strong>
                            </div>
                            <div className="adb-kpi-item">
                                <span>已起飞</span>
                                <strong>{summary.departed}</strong>
                            </div>
                            <div className="adb-kpi-item">
                                <span>有注册号</span>
                                <strong>{summary.with_registration}</strong>
                            </div>
                        </div>
                    </section>

                    <section className="adb-block">
                        <h3>目的地 Top</h3>
                        {summary.top_destinations?.length ? (
                            <ul className="adb-top-list">
                                {summary.top_destinations.map((item) => (
                                    <li key={item.iata}>
                                        <span>
                                            {item.iata}
                                            {item.city ? ` ${item.city}` : ""}
                                        </span>
                                        <strong>{item.count}</strong>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <Text type="secondary">暂无目的地</Text>
                        )}
                    </section>

                    <p className="adb-disclaimer">{meta?.disclaimer || "第三方观察数据，不等于公司正式航班执行记录"}</p>
                </aside>

                <main className="adb-main">
                    <div className="adb-main-meta">
                        <strong>{currentCode ? `${currentCode} 离港列表` : "离港列表"}</strong>
                        <span>来源 {meta?.source === "flightradar24" ? "FR24" : meta?.source || "—"}</span>
                        <span>最近更新 {formatDateTime(meta?.last_updated_at)}</span>
                        <span>本次 {meta?.result_count ?? 0} 条</span>
                    </div>

                    <div className="adb-filters">
                        <div className="adb-filter-row">
                            <Select
                                style={{ width: 140 }}
                                value={status}
                                options={STATUS_OPTIONS}
                                onChange={setStatus}
                            />
                            <Input
                                allowClear
                                style={{ width: 240 }}
                                placeholder="航班号 / 目的地 / 注册号"
                                value={searchInput}
                                onChange={(event) => setSearchInput(event.target.value)}
                                onPressEnter={handleQuery}
                            />
                            <Button type="primary" data-testid="adb-query" onClick={handleQuery}>
                                查询
                            </Button>
                            <Button data-testid="adb-reset" onClick={handleReset}>
                                重置
                            </Button>
                        </div>
                    </div>

                    <Spin spinning={loading}>
                        <Table
                            rowKey="id"
                            size="middle"
                            pagination={false}
                            columns={columns}
                            dataSource={flights}
                            locale={{ emptyText: <Empty description="当前条件暂无离港观察" /> }}
                            onRow={(record) => ({
                                onClick: () => handleRowClick(record),
                                className: record.id === expandedFlightId ? "adb-row-open" : undefined,
                            })}
                        />
                    </Spin>

                    {expandedFlight ? (
                        <section className="adb-detail" aria-label="航班详情">
                            <div className="adb-detail-header">
                                <h3>行详情 · {displayText(expandedFlight.flight_number)}</h3>
                                <div className="adb-detail-actions">
                                    <Button onClick={() => setExpandedFlightId(null)}>关闭详情</Button>
                                    {expandedFlight.source_detail_url ? (
                                        <Button
                                            type="link"
                                            href={expandedFlight.source_detail_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            打开 FR24 详情页
                                        </Button>
                                    ) : (
                                        <Button disabled>打开 FR24 详情页</Button>
                                    )}
                                </div>
                            </div>
                            <div className="adb-detail-grid">
                                <div>
                                    <span>计划起飞</span>
                                    <strong>{formatClock(expandedFlight.scheduled_departure_at, airportTimeZone)}</strong>
                                </div>
                                <div>
                                    <span>预计 / 实际</span>
                                    <strong>
                                        {formatClock(expandedFlight.estimated_departure_at, airportTimeZone)} /{" "}
                                        {formatClock(expandedFlight.actual_departure_at, airportTimeZone)}
                                    </strong>
                                </div>
                                <div>
                                    <span>机位 / 跑道</span>
                                    <strong>
                                        {displayText(expandedFlight.gate)} / {displayText(expandedFlight.runway)}
                                    </strong>
                                </div>
                                <div>
                                    <span>注册号</span>
                                    <strong>{displayText(expandedFlight.aircraft_registration, "未知")}</strong>
                                </div>
                                <div>
                                    <span>航司</span>
                                    <strong>{displayText(expandedFlight.airline_name)}</strong>
                                </div>
                                <div>
                                    <span>机型</span>
                                    <strong>{displayText(expandedFlight.aircraft_type_code)}</strong>
                                </div>
                                <div>
                                    <span>原始状态文案</span>
                                    <strong>{displayText(expandedFlight.status_raw, "未知")}</strong>
                                </div>
                                <div>
                                    <span>来源</span>
                                    <strong>{sourceLabel(meta?.source)}</strong>
                                </div>
                            </div>
                        </section>
                    ) : null}
                </main>
            </div>
        </div>
    );
}
